import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleOff,
  Clock3,
  Eye,
  Inbox,
  LoaderCircle,
  Mail,
  Phone,
  RefreshCcw,
  Search,
  ShieldCheck,
  UserRoundCheck,
  XCircle,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { useAlerts } from "../../components/feedback/AlertProvider";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  activateFreeTrialRequest,
  getFreeTrialRequests,
  reviewFreeTrialRequest,
  sendFreeTrialAccountDetails,
  setFreeTrialActivation,
} from "../../api/freeTrialApi";

import "../../styles/FreeTrial/free-trial-requests-manager.css";

const FILTERS = [
  { value: "all", icon: Inbox },
  { value: "pending", icon: Clock3 },
  { value: "approved", icon: CheckCircle2 },
  { value: "declined", icon: XCircle },
];

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}

function getContactPhone(item) {
  return (
    item.business_phone ||
    item.phone ||
    item.business_whatsapp ||
    ""
  );
}

function getUpdatedRequest(result) {
  return result?.request || result || null;
}

export default function FreeTrialRequestsManager() {
  const navigate = useNavigate();
  const { showAlert } = useAlerts();
  const { t, isRtl } = useLanguage();

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(null);

  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);

  const query = useMemo(
    () => ({
      page,
      ...(status !== "all" ? { status } : {}),
      ...(appliedSearch ? { search: appliedSearch } : {}),
    }),
    [page, status, appliedSearch],
  );

  useEffect(() => {
    let active = true;

    async function loadRequests() {
      setLoading(true);

      try {
        const result = await getFreeTrialRequests(query);

        if (!active) {
          return;
        }

        setItems(Array.isArray(result?.items) ? result.items : []);
        setPagination(
          result?.pagination || {
            current_page: 1,
            last_page: 1,
            total: 0,
          },
        );
      } catch (error) {
        if (!active) {
          return;
        }

        showAlert({
          type: "error",
          message:
            error.userMessage ||
            t("freeTrial.admin.loadError"),
        });
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadRequests();

    return () => {
      active = false;
    };
  }, [query, showAlert, t]);

  function applySearch(event) {
    event.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  }

  function changeFilter(nextStatus) {
    setStatus(nextStatus);
    setPage(1);
  }

  function updateRequestInTable(id, updatedRequest) {
    if (!updatedRequest) {
      return;
    }

    setItems((currentItems) =>
      currentItems.map((item) =>
        item.id === id ? updatedRequest : item,
      ),
    );
  }

  function isRowBusy(id) {
    return busyAction?.id === id;
  }

  function isActionBusy(id, action) {
    return (
      busyAction?.id === id &&
      busyAction?.action === action
    );
  }

  async function reviewFromTable(item, decision) {
    if (
      item.status !== "pending" ||
      isRowBusy(item.id)
    ) {
      return;
    }

    setBusyAction({
      id: item.id,
      action: decision,
    });

    try {
      const reviewResult = await reviewFreeTrialRequest(
        item.id,
        decision,
        "",
      );

      let finalResult = reviewResult;

      /*
       * Keep the existing approval flow, but create the owner account
       * immediately after approval so the accepted requester can log in.
       */
      if (decision === "approved") {
        setBusyAction({
          id: item.id,
          action: "activate",
        });

        finalResult = await activateFreeTrialRequest(
          item.id,
        );
      }

      updateRequestInTable(
        item.id,
        getUpdatedRequest(finalResult),
      );

      showAlert({
        type:
          decision === "approved" &&
          finalResult?.credentials_sent === false
            ? "warning"
            : "success",
        message:
          decision === "approved"
            ? finalResult?.credentials_sent === false
              ? t(
                  "freeTrial.admin.activationEmailWarning",
                )
              : t("freeTrial.admin.activateSuccess")
            : t("freeTrial.admin.declinedMessage"),
      });
    } catch (error) {
      showAlert({
        type: "error",
        message:
          error.userMessage ||
          (decision === "approved"
            ? t("freeTrial.admin.activationError")
            : t("freeTrial.admin.reviewError")),
      });
    } finally {
      setBusyAction(null);
    }
  }

  async function activateFromTable(item) {
    if (
      item.status !== "approved" ||
      item.account?.created ||
      isRowBusy(item.id)
    ) {
      return;
    }

    setBusyAction({
      id: item.id,
      action: "activate",
    });

    try {
      const result = await activateFreeTrialRequest(
        item.id,
      );

      updateRequestInTable(
        item.id,
        getUpdatedRequest(result),
      );

      showAlert({
        type:
          result?.credentials_sent === false
            ? "warning"
            : "success",
        message:
          result?.credentials_sent === false
            ? t(
                "freeTrial.admin.activationEmailWarning",
              )
            : t("freeTrial.admin.activateSuccess"),
      });
    } catch (error) {
      showAlert({
        type: "error",
        message:
          error.userMessage ||
          t("freeTrial.admin.activationError"),
      });
    } finally {
      setBusyAction(null);
    }
  }

  async function toggleActivationFromTable(item) {
    if (
      !item.account?.created ||
      isRowBusy(item.id)
    ) {
      return;
    }

    const nextActive = !item.account.active;

    setBusyAction({
      id: item.id,
      action: nextActive
        ? "reactivate"
        : "deactivate",
    });

    try {
      const result =
        await setFreeTrialActivation(
          item.id,
          nextActive,
        );

      updateRequestInTable(
        item.id,
        getUpdatedRequest(result),
      );

      showAlert({
        type: "success",
        message: nextActive
          ? t("freeTrial.admin.reactivateSuccess")
          : t("freeTrial.admin.deactivateSuccess"),
      });
    } catch (error) {
      showAlert({
        type: "error",
        message:
          error.userMessage ||
          t("freeTrial.admin.activationError"),
      });
    } finally {
      setBusyAction(null);
    }
  }

  async function sendAccountDetailsFromTable(item) {
    if (
      item.status !== "approved" ||
      !item.account?.created ||
      isRowBusy(item.id)
    ) {
      return;
    }

    setBusyAction({
      id: item.id,
      action: "credentials",
    });

    try {
      const result =
        await sendFreeTrialAccountDetails(item.id);

      updateRequestInTable(
        item.id,
        getUpdatedRequest(result),
      );

      showAlert({
        type: "success",
        message: t(
          "freeTrial.admin.accountDetailsSent",
        ),
      });
    } catch (error) {
      showAlert({
        type: "error",
        message:
          error.userMessage ||
          t(
            "freeTrial.admin.accountDetailsError",
          ),
      });
    } finally {
      setBusyAction(null);
    }
  }

  const activeFilter =
    FILTERS.find((item) => item.value === status) ||
    FILTERS[0];

  const ActiveFilterIcon = activeFilter.icon;

  return (
    <div
      className={`free-trial-admin-page${
        isRtl ? " is-rtl" : ""
      }`}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <header className="free-trial-admin-heading">
        <div className="free-trial-admin-heading-icon">
          <ShieldCheck size={25} />
        </div>

        <div>
          <span>{t("freeTrial.admin.kicker")}</span>
          <h1>{t("freeTrial.admin.title")}</h1>
          <p>{t("freeTrial.admin.description")}</p>
        </div>
      </header>

      {loading ? (
        <div
          className="free-trial-admin-loading"
          role="status"
        >
          <LoaderCircle
            className="free-trial-admin-spin"
            size={30}
          />
          <span>
            {t("freeTrial.admin.loading")}
          </span>
        </div>
      ) : (
        <div className="free-trial-admin-layout">
          <aside
            className="free-trial-admin-list"
            aria-label={t(
              "freeTrial.admin.requests",
            )}
          >
            <div className="free-trial-admin-list-header">
              <strong>
                {t("freeTrial.admin.requests")}
              </strong>
              <span>{pagination.total || 0}</span>
            </div>

            {FILTERS.map((filter) => {
              const FilterIcon = filter.icon;
              const isActive =
                status === filter.value;

              return (
                <button
                  key={filter.value}
                  type="button"
                  className={`free-trial-admin-list-item${
                    isActive ? " active" : ""
                  }`}
                  onClick={() =>
                    changeFilter(filter.value)
                  }
                >
                  <span className="free-trial-admin-list-icon">
                    <FilterIcon size={18} />
                  </span>

                  <span className="free-trial-admin-list-copy">
                    <strong>
                      {t(
                        `freeTrial.admin.filters.${filter.value}`,
                      )}
                    </strong>
                    <small>
                      {filter.value === "all"
                        ? t("freeTrial.admin.requests")
                        : t(
                            `freeTrial.admin.status.${filter.value}`,
                          )}
                    </small>
                  </span>

                  {isActive ? (
                    <CheckCircle2
                      size={17}
                      className="free-trial-admin-list-check"
                    />
                  ) : null}
                </button>
              );
            })}
          </aside>

          <section className="free-trial-admin-editor">
            <div className="free-trial-admin-editor-header">
              <div className="free-trial-admin-editor-title">
                <span className="free-trial-admin-editor-icon">
                  <ActiveFilterIcon size={21} />
                </span>

                <div>
                  <span>
                    {t("freeTrial.admin.requests")}
                  </span>
                  <h2>
                    {t(
                      `freeTrial.admin.filters.${status}`,
                    )}
                  </h2>
                </div>
              </div>

              <form
                className="free-trial-admin-search"
                onSubmit={applySearch}
              >
                <Search size={16} />

                <input
                  type="search"
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder={t(
                    "freeTrial.admin.searchPlaceholder",
                  )}
                />

                <button type="submit">
                  {t("common.search")}
                </button>
              </form>
            </div>

            {items.length ? (
              <>
                <div className="free-trial-admin-table-wrap">
                  <table className="free-trial-admin-table">
                    <thead>
                      <tr>
                        <th>
                          {t(
                            "freeTrial.admin.table.requester",
                          )}
                        </th>
                        <th>
                          {t(
                            "freeTrial.admin.table.business",
                          )}
                        </th>
                        <th>
                          {t(
                            "freeTrial.admin.table.contact",
                          )}
                        </th>
                        <th>
                          {t(
                            "freeTrial.admin.table.submitted",
                          )}
                        </th>
                        <th>
                          {t(
                            "freeTrial.admin.table.status",
                          )}
                        </th>
                        <th className="free-trial-admin-actions-head">
                          {t(
                            "freeTrial.admin.table.actions",
                          )}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {items.map((item) => {
                        const phone =
                          getContactPhone(item);

                        const accountCreated =
                          Boolean(
                            item.account?.created,
                          );

                        const accountActive =
                          Boolean(
                            item.account?.active,
                          );

                        const rowBusy =
                          isRowBusy(item.id);

                        return (
                          <tr key={item.id}>
                            <td>
                              <div className="free-trial-admin-person">
                                <span>
                                  {String(
                                    item.applicant_name ||
                                      item.email ||
                                      "?",
                                  )
                                    .charAt(0)
                                    .toUpperCase()}
                                </span>

                                <div>
                                  <strong>
                                    {item.applicant_name ||
                                      "—"}
                                  </strong>
                                  <small>
                                    #{item.id}
                                  </small>
                                </div>
                              </div>
                            </td>

                            <td>
                              <div className="free-trial-admin-business">
                                <strong>
                                  {item.business_name ||
                                    "—"}
                                </strong>

                                <small>
                                  {item.business_type
                                    ? t(
                                        `freeTrial.businessTypes.${item.business_type}`,
                                      )
                                    : "—"}
                                </small>
                              </div>
                            </td>

                            <td>
                              <div className="free-trial-admin-contact">
                                <a
                                  href={`mailto:${item.email}`}
                                  title={item.email}
                                >
                                  <Mail size={13} />
                                  <span>
                                    {item.email}
                                  </span>
                                </a>

                                {phone ? (
                                  <a
                                    href={`tel:${phone}`}
                                    title={phone}
                                  >
                                    <Phone size={13} />
                                    <span>
                                      {phone}
                                    </span>
                                  </a>
                                ) : (
                                  <span>—</span>
                                )}
                              </div>
                            </td>

                            <td>
                              <span className="free-trial-admin-date">
                                {formatDate(item.submitted_at)}
                              </span>
                            </td>

                            <td>
                              <span
                                className={`free-trial-admin-status free-trial-admin-status--${item.status}`}
                              >
                                {t(
                                  `freeTrial.admin.status.${item.status}`,
                                )}
                              </span>
                            </td>

                            <td>
                              <div className="free-trial-admin-actions">
                                {item.status ===
                                  "pending" ? (
                                  <>
                                    <button
                                      type="button"
                                      className="free-trial-admin-action free-trial-admin-action--approve"
                                      disabled={
                                        rowBusy
                                      }
                                      onClick={() =>
                                        reviewFromTable(
                                          item,
                                          "approved",
                                        )
                                      }
                                      title={t(
                                        "freeTrial.admin.approve",
                                      )}
                                    >
                                      {isActionBusy(
                                        item.id,
                                        "approved",
                                      ) ? (
                                        <LoaderCircle
                                          className="free-trial-admin-spin"
                                          size={15}
                                        />
                                      ) : (
                                        <CheckCircle2
                                          size={15}
                                        />
                                      )}
                                    </button>

                                    <button
                                      type="button"
                                      className="free-trial-admin-action free-trial-admin-action--decline"
                                      disabled={
                                        rowBusy
                                      }
                                      onClick={() =>
                                        reviewFromTable(
                                          item,
                                          "declined",
                                        )
                                      }
                                      title={t(
                                        "freeTrial.admin.decline",
                                      )}
                                    >
                                      {isActionBusy(
                                        item.id,
                                        "declined",
                                      ) ? (
                                        <LoaderCircle
                                          className="free-trial-admin-spin"
                                          size={15}
                                        />
                                      ) : (
                                        <XCircle
                                          size={15}
                                        />
                                      )}
                                    </button>
                                  </>
                                ) : null}

                                {item.status ===
                                  "approved" &&
                                !accountCreated ? (
                                  <button
                                    type="button"
                                    className="free-trial-admin-action free-trial-admin-action--activate"
                                    disabled={
                                      rowBusy
                                    }
                                    onClick={() =>
                                      activateFromTable(
                                        item,
                                      )
                                    }
                                    title={t(
                                      "freeTrial.admin.activate",
                                    )}
                                  >
                                    {isActionBusy(
                                      item.id,
                                      "activate",
                                    ) ? (
                                      <LoaderCircle
                                        className="free-trial-admin-spin"
                                        size={15}
                                      />
                                    ) : (
                                      <UserRoundCheck
                                        size={15}
                                      />
                                    )}
                                  </button>
                                ) : null}

                                {item.status ===
                                  "approved" &&
                                accountCreated ? (
                                  <>
                                    <button
                                      type="button"
                                      className="free-trial-admin-action free-trial-admin-action--email"
                                      disabled={
                                        rowBusy
                                      }
                                      onClick={() =>
                                        sendAccountDetailsFromTable(
                                          item,
                                        )
                                      }
                                      title={t(
                                        "freeTrial.admin.sendAccountDetails",
                                      )}
                                    >
                                      {isActionBusy(
                                        item.id,
                                        "credentials",
                                      ) ? (
                                        <LoaderCircle
                                          className="free-trial-admin-spin"
                                          size={15}
                                        />
                                      ) : (
                                        <Mail
                                          size={15}
                                        />
                                      )}
                                    </button>

                                    <button
                                      type="button"
                                      className={`free-trial-admin-action ${
                                        accountActive
                                          ? "free-trial-admin-action--deactivate"
                                          : "free-trial-admin-action--reactivate"
                                      }`}
                                      disabled={
                                        rowBusy
                                      }
                                      onClick={() =>
                                        toggleActivationFromTable(
                                          item,
                                        )
                                      }
                                      title={
                                        accountActive
                                          ? t(
                                              "freeTrial.admin.deactivate",
                                            )
                                          : t(
                                              "freeTrial.admin.reactivate",
                                            )
                                      }
                                    >
                                      {isActionBusy(
                                        item.id,
                                        accountActive
                                          ? "deactivate"
                                          : "reactivate",
                                      ) ? (
                                        <LoaderCircle
                                          className="free-trial-admin-spin"
                                          size={15}
                                        />
                                      ) : accountActive ? (
                                        <CircleOff
                                          size={15}
                                        />
                                      ) : (
                                        <RefreshCcw
                                          size={15}
                                        />
                                      )}
                                    </button>
                                  </>
                                ) : null}

                                <button
                                  type="button"
                                  className="free-trial-admin-action free-trial-admin-action--view"
                                  onClick={() =>
                                    navigate(
                                      `/free-trial-requests/${item.id}`,
                                    )
                                  }
                                  title={t(
                                    "freeTrial.admin.viewDetails",
                                  )}
                                >
                                  <Eye size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {pagination.last_page > 1 ? (
                  <footer className="free-trial-admin-pagination">
                    <span>
                      {pagination.current_page ||
                        page}{" "}
                      /{" "}
                      {pagination.last_page || 1}
                    </span>

                    <div>
                      <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() =>
                          setPage((current) =>
                            Math.max(
                              1,
                              current - 1,
                            ),
                          )
                        }
                      >
                        {isRtl ? (
                          <ChevronRight
                            size={15}
                          />
                        ) : (
                          <ChevronLeft
                            size={15}
                          />
                        )}
                        {t("common.previous")}
                      </button>

                      <button
                        type="button"
                        disabled={
                          page >=
                          pagination.last_page
                        }
                        onClick={() =>
                          setPage((current) =>
                            Math.min(
                              pagination.last_page,
                              current + 1,
                            ),
                          )
                        }
                      >
                        {t("common.next")}
                        {isRtl ? (
                          <ChevronLeft
                            size={15}
                          />
                        ) : (
                          <ChevronRight
                            size={15}
                          />
                        )}
                      </button>
                    </div>
                  </footer>
                ) : null}
              </>
            ) : (
              <div className="free-trial-admin-empty">
                <Mail size={29} />
                <strong>
                  {t("freeTrial.admin.empty")}
                </strong>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}